import { useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import {
    GET_BOL_PRINT_DATA,
    GetBolPrintDataResponse,
    GetBolPrintDataVariables
} from '../graphql/queries/getTmsTaskBolInfos';
import { StationLoadingDetailDto } from '../graphql/queries/getStationLoadingDetails';
import { BolDataV3, BolStop, BolCarrierInfo, StationLoadingDetail } from '../components/bol/v3/types/bolTypes';
import { kgToLb } from '../components/bol/v3/ts/bolUtils';
import { t } from '../../../../i18n';
import { TaskType } from '../types';

/**
 * BOL v3 生成 Hook（BOL Of Loading，非 TikTok 任务使用）
 * 提单信息与装车明细（Loading Information）由 GET_BOL_PRINT_DATA 一次取回，打开预览时数据已齐
 */
export const useBolGenerationV3 = () => {
    const [getBolPrintData] = useLazyQuery<GetBolPrintDataResponse, GetBolPrintDataVariables>(
        GET_BOL_PRINT_DATA,
        { fetchPolicy: 'network-only' }
    );

    /** 服务端重量为 kg（bag.weight_kg），单据以 LB 展示，此处统一换算 */
    const toLoadingDetails = (stations: StationLoadingDetailDto[] | undefined): StationLoadingDetail[] =>
        (stations ?? []).map(station => ({
            ...station,
            bags: station.bags.map(bag => ({
                bagNumber: bag.bagNumber,
                parcelCount: bag.parcelCount,
                weight: bag.weightKg != null ? kgToLb(bag.weightKg) : null
            }))
        }));

    const buildStopFromBolInfo = (bolInfo: any, segment: number): BolStop => {
        const formatStr = 'MM/DD/YYYY';
        const formattedPlanArrivalLocalDate = bolInfo.planArrivalLocalDate
            ? dayjs(bolInfo.planArrivalLocalDate).format(formatStr)
            : '';

        const fromSortCode = bolInfo.shipFromWarehouseSortingCode || '';
        const toSortCode = bolInfo.shipToWarehouseSortingCode || '';

        const formattedStaticEtaDate = bolInfo.staticEtaDate
            ? dayjs(bolInfo.staticEtaDate).format(formatStr)
            : '';

        return {
            segment,
            shipFromId: bolInfo.shipFromWarehouseId?.toString() || '',
            shipFromAddress: bolInfo.shipFromAddress || '',
            shipFromContactName: bolInfo.shipFromContactName || '',
            shipFromContactPhoneNumber: bolInfo.shipFromContactPhoneNumber || '',
            shipFromSortCode: fromSortCode,
            shipToId: bolInfo.shipToWarehouseId?.toString() || '',
            shipToAddress: bolInfo.shipToAddress || '',
            shipToCityStateZipcode: bolInfo.shipToCityStateZipcode || '',
            shipToContactName: bolInfo.shipToContactName || '',
            shipToContactPhoneNumber: bolInfo.shipToContactPhoneNumber || '',
            shipToSortCode: toSortCode,
            planArrivalLocalDate: formattedPlanArrivalLocalDate || null,
            planArrivalLocalTime: bolInfo.planArrivalLocalTime || null,
            planArrivalLocalTimezone: bolInfo.nextStopLocalTimezone || null,
            planArrivalTimezoneLocation: bolInfo.nextStopTimezoneLocation || null,
            staticEtaDate: formattedStaticEtaDate || null,
            staticEtaTime: bolInfo.staticEtaTime || null,
            staticEtaLocalTimezone: bolInfo.nextStopLocalTimezone || null,
            staticEtaTimezoneLocation: bolInfo.nextStopTimezoneLocation || null,
            sealNumber: bolInfo?.sealNumber || '',
            // 签名表实际到离时间/签名：后端提供后在此映射
            arriveTime: bolInfo.arriveTime || null,
            departTime: bolInfo.departTime || null,
            driverSignature: bolInfo.driverSignature || null,
            warehouseSignature: bolInfo.warehouseSignature || null
        };
    };

    const generateBolDataV3 = (task: any, bolInfos: any[], loadingDetails: StationLoadingDetail[] = []): BolDataV3 => {
        const sortedBolInfos = [...bolInfos].sort((a, b) => a.segment - b.segment);

        if (sortedBolInfos.length === 0) {
            throw new Error('No printable BOL');
        }

        const firstBolInfo = sortedBolInfos[0];
        const formatStr = 'MM/DD/YYYY';
        const taskDate = firstBolInfo.taskDate || task.taskDate || new Date().toISOString().split('T')[0];
        const formattedTaskDate = taskDate ? dayjs(taskDate).format(formatStr) : '';

        const carrier: BolCarrierInfo = {
            carrierName: firstBolInfo?.carrierName || task.carrierName || '',
            trailerNumber: firstBolInfo?.trailerNumber || '',
            truckNumber: firstBolInfo?.truckNumber || '',
            licensePlate: firstBolInfo?.licensePlate || '',
            proNumber: firstBolInfo?.proNumbers,
            vehicleModel: firstBolInfo?.vehicleModel || '',
            // 司机1/司机2 均来自任务侧实时值（tms_task + tms_task_ext），BOL 上不可编辑
            driver1Name: firstBolInfo?.driver1Name,
            driver1Phone: firstBolInfo?.driver1Phone,
            // 第二司机：无值时单据留空
            driver2Name: firstBolInfo?.driver2Name,
            driver2Phone: firstBolInfo?.driver2Phone
        };

        const stops: BolStop[] = [];
        // stops[0]: 始发站（SHIP FROM）
        stops.push({
            segment: 0,
            shipFromId: '',
            shipFromAddress: '',
            shipFromContactName: '',
            shipFromContactPhoneNumber: '',
            shipFromSortCode: '',
            shipToId: firstBolInfo.shipFromWarehouseId?.toString() || '',
            shipToAddress: firstBolInfo.shipFromAddress || '',
            shipToCityStateZipcode: firstBolInfo.shipFromCityStateZipcode || '',
            shipToContactName: firstBolInfo.shipFromContactName || '',
            shipToContactPhoneNumber: firstBolInfo.shipFromContactPhoneNumber || '',
            shipToSortCode: firstBolInfo.shipFromWarehouseSortingCode || '',
            planArrivalLocalDate: null,
            planArrivalLocalTime: null,
            planArrivalLocalTimezone: null,
            planArrivalTimezoneLocation: null,
            sealNumber: ''
        });

        for (const bolInfo of sortedBolInfos) {
            stops.push(buildStopFromBolInfo(bolInfo, bolInfo.segment));
        }

        return {
            taskId: task.id?.toString() || '',
            date: formattedTaskDate,
            routeName: task.routeName || '',
            routeCode: task.routeCode || '',
            billOfLadingNumber: firstBolInfo.bolNumber || '',
            freightOrderId: firstBolInfo.freightOrderId || '',
            businessType: task.businessType,
            taskType: task.taskType,
            routeType: task.routeType,
            carrierSourceType: task.carrierSourceType,
            carrier,
            stops,
            // Remark 取任务备注（tms_task.remark），BOL 快照表无此字段
            remark: task.remark || '',
            loadingDetails,
            weightUnit: 'LB'
        };
    };

    const handleBolPrint = async (record: TaskType) => {
        try {
            const taskId = record.id;

            const { data } = await getBolPrintData({
                variables: { taskId: taskId.toString(), taskIdLong: Number(taskId) }
            });

            if (!data?.getTmsTaskBolInfosByTaskId || data.getTmsTaskBolInfosByTaskId.length === 0) {
                throw new Error(t('tms.task.bol.errors.bolInfoNotFound'));
            }

            const bolInfos = data.getTmsTaskBolInfosByTaskId;
            const task = {
                id: taskId,
                routeName: record.routeName,
                routeCode: record.routeCode,
                taskDate: record.taskDate,
                carrierName: record.carrierName,
                licensePlate: record.licensePlate,
                routeType: record.routeType,
                taskType: record.taskType,
                businessType: record.businessType,
                carrierSourceType: record.carrierSourceType,
                remark: record.remark
            };

            const bolData = generateBolDataV3(
                task,
                bolInfos,
                toLoadingDetails(data.getStationLoadingDetailsByTaskId)
            );
            return { success: true, bolData, task };
        } catch (error) {
            console.error('Failed to get BOL infos (v3):', error);
            const errorMessage = error instanceof Error ? error.message : t('tms.task.bol.errors.bolInfoNotFound');
            return { success: false, error: errorMessage };
        }
    };

    return {
        generateBolData: generateBolDataV3,
        handleBolPrint
    };
};
